import { escapeHtmlAttr } from '@shared/utils/helpers';

/**
 * Represents a product category returned by the CatalogApiController.GetProductGroups endpoint.
 */
export interface ProductCategory {
    Id: string;
    SeoFriendlyId: string;
    Name: string;
    MediaURL: string;
    ProductCount: number;
    Description: string;
    CortOrder: number;
    Children: ProductCategory[];
}

/**
 * Configuration options read from the data attributes of the category grid container element.
 */
export interface CategoryGridConfig {
    /** Maximum number of categories to display. */
    maxItems: number;
    /** Base URL for the products page (e.g. /Catalog/Products). */
    productsBaseUrl: string;
    /** URL of the GetProductGroups API endpoint. */
    apiUrl: string;
    /** Whether to include item IDs in the response. */
    withItemIds: boolean;
    /** Whether to include offer categories in the response. */
    withOfferCategory: boolean;
    /** The start assortment ID to use as the root (e.g. WS_START). */
    startAssortment: string;
}

/**
 * Manages a dynamic category grid on the home page.
 * Reads configuration from the container's data attributes, fetches product categories
 * from the CatalogApi endpoint and renders category cards into the grid.
 */
export class CategoryGridManager {
    private readonly container: HTMLElement;
    private readonly config: CategoryGridConfig;

    /**
     * @param container - The DOM element that wraps the category grid and holds data attributes.
     */
    constructor(container: HTMLElement) {
        this.container = container;
        this.config = this.readConfig();
        this.initialize();
    }

    /**
     * Reads the data attributes from the container element and builds the configuration object.
     *
     * @returns The parsed configuration for this category grid instance.
     */
    private readConfig(): CategoryGridConfig {
        const dataset = this.container.dataset;

        return {
            maxItems: parseInt(dataset.maxItems ?? '3', 10),
            productsBaseUrl: dataset.productsBaseUrl ?? '/Catalog/Products',
            apiUrl: dataset.apiUrl ?? '/CatalogApi/GetProductGroups',
            withItemIds: dataset.withItemIds !== 'false',
            withOfferCategory: dataset.withOfferCategory !== 'false',
            startAssortment: dataset.startAssortment ?? 'WS_START',
        };
    }

    /**
     * Initialises the component: renders a skeleton loader, fetches data and then renders the result.
     *
     * @returns A promise that resolves when initialisation is complete.
     */
    private async initialize(): Promise<void> {
        this.renderSkeleton();

        try {
            const categories = await this.fetchCategories();
            this.renderCategories(categories);
        } catch (error) {
            console.error('[CategoryGrid] Failed to load categories:', error);
            this.renderError();
        }
    }

    /**
     * Fetches product categories from the CatalogApiController.GetProductGroups endpoint.
     *
     * @returns A promise resolving to an array of ProductCategory objects.
     */
    private async fetchCategories(): Promise<ProductCategory[]> {
        const params = new URLSearchParams({
            withItemIds: String(this.config.withItemIds),
            withOfferCategory: String(this.config.withOfferCategory),
            startassortment: this.config.startAssortment,
        });

        const response = await fetch(`${this.config.apiUrl}?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`API responded with status ${response.status}`);
        }

        const allCategories = await response.json() as ProductCategory[];

        // Limit the result to the configured maxItems count
        return allCategories.slice(0, this.config.maxItems);
    }

    /**
     * Renders skeleton placeholder cards while the real data is being fetched.
     * The number of skeletons matches the configured maxItems value.
     */
    private renderSkeleton(): void {
        const grid = this.container.querySelector<HTMLElement>('.category-grid-items');
        if (!grid) return;

        grid.innerHTML = '';

        for (let i = 0; i < this.config.maxItems; i++) {
            const card = document.createElement('div');
            card.className = 'flex flex-1 flex-col gap-8 items-start min-w-0 overflow-hidden animate-pulse';
            card.innerHTML = `
                <div class="h-60 rounded-2xl w-full bg-gray-200"></div>
                <div class="flex flex-col gap-6 items-center w-full">
                    <div class="h-8 bg-gray-200 rounded w-3/4"></div>
                    <div class="h-4 bg-gray-200 rounded w-full"></div>
                    <div class="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>`;
            grid.appendChild(card);
        }
    }

    /**
     * Renders category cards from the fetched data into the grid element.
     *
     * @param categories - The list of categories to render.
     */
    private renderCategories(categories: ProductCategory[]): void {
        const grid = this.container.querySelector<HTMLElement>('.category-grid-items');
        if (!grid) return;

        grid.innerHTML = '';

        if (categories.length === 0) {
            this.renderEmpty(grid);
            return;
        }

        categories.forEach(category => {
            const card = this.createCategoryCard(category);
            grid.appendChild(card);
        });
    }

    /**
     * Creates a single category card element matching the existing static category card design.
     *
     * @param category - The category data to render.
     * @returns A fully constructed HTMLElement representing the card.
     */
    private createCategoryCard(category: ProductCategory): HTMLElement {
        const card = document.createElement('div');
        card.className = 'flex flex-1 flex-col gap-8 items-start min-w-0 overflow-hidden';

        const categoryUrl = `${this.config.productsBaseUrl}?assm=${encodeURIComponent(category.SeoFriendlyId ?? category.Id)}`;

        const hasImage = category.MediaURL
            && !category.MediaURL.includes('00000000-0000-0000-0000-000000000000');

        const imageSrc = hasImage
            ? category.MediaURL
            : 'https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&q=80&w=600';

        // Only render the description paragraph when a non-empty description is available
        const descriptionHtml = category.Description
            ? `<p class="font-['Asap'] font-normal text-[#303e48] text-[16px] leading-[1.3] tracking-[-0.16px] text-center w-full">
                   ${escapeHtmlAttr(category.Description)}
               </p>`
            : '';

        card.innerHTML = `
            <div class="h-60 relative rounded-2xl w-full overflow-hidden">
                <img
                    src="${escapeHtmlAttr(imageSrc)}"
                    alt="${escapeHtmlAttr(category.Name)}"
                    class="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    loading="lazy">
            </div>
            <div class="flex flex-col gap-6 items-center w-full">
                <h3 class="font-['Asap'] font-medium text-[#303e48] text-[32px] leading-[1.3] tracking-[-0.32px] text-center w-full">
                    ${escapeHtmlAttr(category.Name)}
                </h3>
                ${descriptionHtml}
                <a href="${escapeHtmlAttr(categoryUrl)}"
                   class="flex gap-2 items-center text-company font-['Asap'] font-medium text-[16px] leading-[1.3] tracking-[-0.16px] hover:text-company-hover transition-colors">
                    Zu den Produkten
                    <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5l8 7-8 7"/></svg>
                </a>
            </div>`;

        return card;
    }

    /**
     * Renders an empty-state message when the API returns no categories.
     *
     * @param grid - The grid container element to render the message into.
     */
    private renderEmpty(grid: HTMLElement): void {
        grid.innerHTML = `
            <p class="font-['Asap'] text-[#646e76] text-[16px] col-span-full py-8 text-center w-full">
                Keine Kategorien verfügbar.
            </p>`;
    }

    /**
     * Renders an error message inside the grid when the API call fails.
     */
    private renderError(): void {
        const grid = this.container.querySelector<HTMLElement>('.category-grid-items');
        if (!grid) return;

        grid.innerHTML = `
            <p class="font-['Asap'] text-red-500 text-[16px] col-span-full py-8 text-center w-full">
                Die Kategorien konnten nicht geladen werden. Bitte versuche es später erneut.
            </p>`;
    }

}

